import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import { apiFetch, getApiHost } from '../utils/api';

const API_HOST = getApiHost();

/**
 * Cultural research for a single token — the hand-written, progressively revised
 * record, as distinct from the automated social scan on the token detail page.
 *
 * Unlinked from the site nav (same posture as /living-dangerously): reachable at
 * /token/:mint/culture if you know the URL.
 */

type ClaimStatus = 'verified' | 'refuted' | 'unresolved';

interface Claim {
  statement: string;
  status: ClaimStatus;
  method?: string;
  evidenceUrl?: string;
  checkedAt?: string;
  /** Who made the claim. `prior-note` = deepnets itself, not the community. */
  source?: 'community' | 'project' | 'promoter' | 'metadata' | 'prior-note' | string;
}

interface Note {
  analysis: string;
  assessment: string | null;
  claims: Claim[];
  theme: string | null;
  tags: string[];
  references: string[];
  addedBy: string | null;
  addedAt: string | null;
}

interface Summary {
  text: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface CultureResponse {
  mint: string;
  tokenName: string | null;
  tokenSymbol: string | null;
  updatedAt: string | null;
  summary: Summary | null;
  noteCount: number;
  claimCounts: Record<ClaimStatus, number>;
  notes: Note[];
}

const CLAIM_STYLE: Record<ClaimStatus, { chip: string; icon: string; label: string }> = {
  verified:   { chip: 'border-crypto-green/40 bg-crypto-green/10 text-crypto-green', icon: 'fa-check',            label: 'Verified' },
  refuted:    { chip: 'border-crypto-red/40 bg-crypto-red/10 text-crypto-red',       icon: 'fa-xmark',            label: 'Refuted' },
  unresolved: { chip: 'border-crypto-yellow/40 bg-crypto-yellow/10 text-crypto-yellow', icon: 'fa-question',      label: 'Unresolved' },
};

const THEME_STYLE: Record<string, string> = {
  culture:    'border-crypto-accent/30 text-crypto-accent',
  provenance: 'border-blue-400/30 text-blue-300',
  correction: 'border-crypto-yellow/30 text-crypto-yellow',
  ideology:   'border-purple-400/30 text-purple-300',
  community:  'border-teal-400/30 text-teal-300',
};

function formatDate(ts?: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Prose rendering ────────────────────────────────────────────────────
// The notes use a small, stable markup subset (bold, italic, inline code,
// `*** SECTION ***` headers, bullet/numbered lists, and occasional pipe
// tables). Rendering that subset directly avoids pulling in a markdown
// dependency the project does not otherwise carry.

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*|https?:\/\/[^\s)]+)/g;

const renderInline = (text: string): React.ReactNode[] =>
  text.split(INLINE).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="font-mono text-xs bg-black/40 border border-gray-700 rounded px-1 py-0.5 text-gray-300 break-all">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i} className="italic text-gray-300">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('http')) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-crypto-accent hover:underline break-all">{part}</a>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isDivider  = (l: string) => /^\s*\|[\s|:-]+\|\s*$/.test(l);
const splitRow   = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());

const Prose: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // *** SECTION HEADER ***
    const hdr = line.match(/^\s*\*\*\*\s*(.+?)\s*\*\*\*\s*$/);
    if (hdr) {
      blocks.push(
        <h3 key={key++} className="text-[11px] font-bold tracking-[0.12em] uppercase text-crypto-accent/90 mt-6 mb-2">
          {hdr[1].replace(/\*\*/g, '')}
        </h3>,
      );
      i++; continue;
    }

    // Pipe table
    if (isTableRow(line)) {
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        if (!isDivider(lines[i])) rows.push(splitRow(lines[i]));
        i++;
      }
      const [head, ...body] = rows;
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto">
          <table className="text-sm min-w-full border border-gray-700 rounded">
            {head && (
              <thead>
                <tr className="bg-black/30">
                  {head.map((c, ci) => (
                    <th key={ci} className="text-left font-semibold text-gray-300 px-3 py-1.5 border-b border-gray-700 whitespace-nowrap">
                      {renderInline(c)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri} className="border-b border-gray-800 last:border-0">
                  {r.map((c, ci) => (
                    <td key={ci} className="px-3 py-1.5 text-gray-400 align-top">{renderInline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Bullet / numbered list
    const listRe = /^\s*(?:[-•]|\d+[.)])\s+/;
    if (listRe.test(line)) {
      const items: string[] = [];
      while (i < lines.length && listRe.test(lines[i])) {
        items.push(lines[i].replace(listRe, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-2 space-y-1 pl-1">
          {items.map((it, ii) => (
            <li key={ii} className="text-sm text-gray-400 leading-relaxed flex gap-2">
              <span className="text-gray-600 select-none">·</span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph — gather until blank line or a block starts
    const para: string[] = [];
    while (
      i < lines.length && lines[i].trim() &&
      !isTableRow(lines[i]) && !listRe.test(lines[i]) &&
      !/^\s*\*\*\*\s*.+\s*\*\*\*\s*$/.test(lines[i])
    ) {
      para.push(lines[i]); i++;
    }
    blocks.push(
      <p key={key++} className="text-sm text-gray-400 leading-relaxed my-2">{renderInline(para.join(' '))}</p>,
    );
  }

  return <>{blocks}</>;
};

// ── Claims ─────────────────────────────────────────────────────────────

const ClaimRow: React.FC<{ claim: Claim }> = ({ claim }) => {
  const s = CLAIM_STYLE[claim.status] ?? CLAIM_STYLE.unresolved;
  return (
    <div className="border border-gray-700 rounded-lg p-3 bg-black/20">
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 ${s.chip}`}>
          <i className={`fas ${s.icon}`} /> {s.label}
        </span>
        {claim.source && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">
            {/* `prior-note` means deepnets made the claim, not the community. */}
            {claim.source === 'prior-note' ? 'deepnets (prior note)' : claim.source}
          </span>
        )}
        {claim.checkedAt && <span className="shrink-0 text-[10px] text-gray-600 py-0.5">checked {claim.checkedAt}</span>}
      </div>
      <p className="text-sm text-gray-200 mt-2">{renderInline(claim.statement)}</p>
      {claim.method && <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{renderInline(claim.method)}</p>}
      {claim.evidenceUrl && (
        <a href={claim.evidenceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-crypto-accent hover:underline mt-1.5 inline-block break-all">
          <i className="fas fa-link mr-1" />{claim.evidenceUrl}
        </a>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────

const TokenCulturePage: React.FC = () => {
  const { mint } = useParams<{ mint: string }>();
  const [data, setData] = useState<CultureResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mint) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`${API_HOST}/api/token-culture/${mint}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mint]);

  const name = data?.tokenName || mint?.slice(0, 8) || '';
  const symbol = data?.tokenSymbol || '';
  const counts = data?.claimCounts;
  const totalClaims = counts ? counts.verified + counts.refuted + counts.unresolved : 0;

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      {/* Internal page — keep it out of search results. */}
      <Helmet>
        <title>{`Culture research · ${name}${symbol ? ` ($${symbol})` : ''}`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <Link to={`/token/${mint}`} className="text-sm text-gray-400 hover:text-crypto-accent transition-colors">
            <i className="fas fa-arrow-left mr-1" /> Back to {name} {symbol && `($${symbol})`}
          </Link>
        </div>

        <h1 className="text-xl font-bold mb-1">Culture Research</h1>
        <p className="text-sm text-gray-400 mb-4">
          {name} {symbol && <span className="text-gray-500">({symbol})</span>}
          <span className="ml-2 text-gray-600 text-xs font-mono break-all">{mint}</span>
        </p>

        {/* The current read, written for someone who has never heard of the coin.
            Leads the page — everything below it is the working record behind it. */}
        {!loading && !error && data?.summary?.text && (
          <section className="border border-gray-700 bg-crypto-card rounded-xl p-4 sm:p-5 mb-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-crypto-accent/90 mb-2">
              What this is
            </div>
            <div className="text-[15px] leading-relaxed [&>p]:text-gray-200 [&>p]:text-[15px]">
              <Prose text={data.summary.text} />
            </div>
            {data.summary.updatedAt && (
              <div className="text-[11px] text-gray-600 mt-3 pt-2 border-t border-gray-800">
                Current as of {formatDate(data.summary.updatedAt)}
              </div>
            )}
          </section>
        )}

        <p className="text-xs text-gray-500 border border-gray-800 rounded-lg p-3 mb-6 leading-relaxed">
          Hand-written research into what a token&rsquo;s community actually is &mdash; where the meme came from, who
          holds it, and what its members say. Revised over time rather than scored once, so later notes correct
          earlier ones and the newest is shown first. Separate from the automated social scan on the token page,
          which measures mention volume and sentiment.
        </p>

        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="bg-crypto-gray border border-gray-700 rounded-xl p-4 animate-pulse">
                <div className="h-4 w-40 bg-gray-700 rounded mb-3" />
                <div className="h-3 w-full bg-gray-700 rounded mb-2" />
                <div className="h-3 w-5/6 bg-gray-700 rounded mb-2" />
                <div className="h-3 w-2/3 bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-16">
            <i className="fas fa-exclamation-triangle text-crypto-red text-4xl mb-4 block" />
            <p className="text-gray-400">{error}</p>
          </div>
        )}

        {!loading && !error && data && data.notes.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <i className="fas fa-book text-4xl mb-4 block" />
            <p>No culture research recorded for this token yet.</p>
            <p className="text-xs text-gray-600 mt-2">
              Coverage is deliberately partial &mdash; absence here is not a finding about the token.
            </p>
          </div>
        )}

        {!loading && !error && data && data.notes.length > 0 && (
          <>
            {/* Summary bar */}
            <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
              <span className="text-gray-500">
                {data.noteCount} {data.noteCount === 1 ? 'note' : 'notes'}
              </span>
              {totalClaims > 0 && counts && (
                <>
                  <span className="text-gray-700">·</span>
                  {(['verified', 'refuted', 'unresolved'] as ClaimStatus[])
                    .filter(k => counts[k] > 0)
                    .map(k => (
                      <span key={k} className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 ${CLAIM_STYLE[k].chip}`}>
                        <i className={`fas ${CLAIM_STYLE[k].icon}`} /> {counts[k]} {CLAIM_STYLE[k].label.toLowerCase()}
                      </span>
                    ))}
                </>
              )}
              {data.updatedAt && (
                <>
                  <span className="text-gray-700">·</span>
                  <span className="text-gray-600">updated {formatDate(data.updatedAt)}</span>
                </>
              )}
            </div>

            <div className="space-y-5">
              {data.notes.map((note, idx) => (
                <article key={idx} className="bg-crypto-card border border-gray-700 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      {note.theme && (
                        <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 ${THEME_STYLE[note.theme] || 'border-gray-600 text-gray-400'}`}>
                          {note.theme}
                        </span>
                      )}
                      {idx === 0 && data.notes.length > 1 && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">
                          current
                        </span>
                      )}
                    </div>
                    {note.addedAt && <span className="text-xs text-gray-600">{formatDate(note.addedAt)}</span>}
                  </div>

                  {/* Claims first: the adjudicated facts, before the prose. */}
                  {note.claims.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {note.claims.map((c, ci) => <ClaimRow key={ci} claim={c} />)}
                    </div>
                  )}

                  <Prose text={note.analysis} />

                  {/* Judgement, kept visually separate from the description. */}
                  {note.assessment && (
                    <div className="mt-4 pt-3 border-t border-gray-800">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 mb-1">
                        Assessment
                      </div>
                      <div className="opacity-90">
                        <Prose text={note.assessment} />
                      </div>
                    </div>
                  )}

                  {note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {note.tags.map((t, ti) => (
                        <span key={ti} className="text-[10px] text-gray-500 bg-black/30 border border-gray-800 rounded px-1.5 py-0.5">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  {note.references.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400 select-none">
                        {note.references.length} {note.references.length === 1 ? 'source' : 'sources'}
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {note.references.map((r, ri) => (
                          <li key={ri}>
                            <a href={r} target="_blank" rel="noopener noreferrer" className="text-xs text-crypto-accent hover:underline break-all">
                              {r}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TokenCulturePage;
